import { useEffect, useRef } from 'react';
import Swal from 'sweetalert2';
import { AudioControllers } from './useAudio';
import { useSettingsStore } from '../store/settingsStore';
import { Profile, SubAlarm } from '../store/profileStore';
import { playKidSound } from '../utils/kidSounds';
import { playThemeSound, stopThemeSound } from '../utils/themeSounds';
import { speak } from '../utils/speech';

type UseSubAlarmsOptions = {
    /** Active profile (null/undefined = no sub-alarms). */
    profile?: Profile | null;
    /** Total timer duration in minutes. */
    totalMinutes: number;
    /** Current countdown value in seconds (decrements from totalMinutes*60 to 0). */
    count: number;
    /** Whether the timer is currently running. */
    isRunning: boolean;
    /** Audio controller for playing sub-alarm sounds (for URL-based sounds). */
    audio: AudioControllers;
};

const playSoundSrc = (src: string, audio: AudioControllers, loopStopMs = 4000) => {
    if (src.startsWith('kid:')) {
        playKidSound(src.slice(4));
    } else if (src.startsWith('http')) {
        stopThemeSound();
        playThemeSound(src);
    } else if (src) {
        audio.pause();
        audio.reset();
        audio.play(src);
        setTimeout(() => {
            audio.pause();
            audio.reset();
        }, loopStopMs);
    } else {
        audio.pause();
        audio.reset();
        audio.play();
        setTimeout(() => {
            audio.pause();
            audio.reset();
        }, loopStopMs);
    }
};

const fireSubAlarm = (sub: SubAlarm, audio: AudioControllers, voiceEnabled: boolean) => {
    playSoundSrc(sub.soundSrc, audio);
    if (voiceEnabled) {
        // Speak the label (strip emoji) a moment after the sound starts
        setTimeout(() => speak(sub.label.replace(/[^\p{L}\p{N}\s!]/gu, '').trim()), 300);
    }
    Swal.fire({
        toast: true,
        position: 'top',
        title: `${sub.emoji || '🔔'} ${sub.label}`,
        text: `${sub.atElapsedMin} min done! 👍`,
        showConfirmButton: false,
        timer: 5000,
        timerProgressBar: true,
        customClass: { popup: 'rounded-2xl' },
    });
};

const fireRecurringWarning = (
    elapsedMin: number,
    audio: AudioControllers,
    recurring: NonNullable<Profile['recurringWarning']>,
    voiceEnabled: boolean
) => {
    playSoundSrc(recurring.soundSrc, audio, 1500);
    if (voiceEnabled) {
        setTimeout(() => speak(`${elapsedMin} minutes elapsed`, { volume: 0.6 }), 200);
    }
    Swal.fire({
        toast: true,
        position: 'top',
        title: `${recurring.emoji} ${recurring.label}`,
        text: `${elapsedMin} min elapsed`,
        showConfirmButton: false,
        timer: 2500,
        timerProgressBar: true,
        customClass: { popup: 'rounded-2xl' },
    });
};

/**
 * Fires sub-alarm sounds + toasts at elapsed-minute thresholds defined in the
 * active profile. Sub-alarms whose `atElapsedMin` equals `totalMinutes` are
 * SKIPPED here because the timer's `onFinish` handler owns the final alarm.
 *
 * Also fires a recurring warning every N minutes if the profile defines one
 * (e.g. Exam mode: low beep every 1 minute).
 *
 * Also plays a tick sound every second while running, if enabled in settings.
 *
 * Returns the currently-active sub-alarm (the most recent threshold passed)
 * so the UI can change phase color/label, or null if none apply.
 */
export const useSubAlarms = ({
    profile,
    totalMinutes,
    count,
    isRunning,
    audio,
}: UseSubAlarmsOptions): SubAlarm | null => {
    const firedRef = useRef<Set<string>>(new Set());
    const lastRecurringMinRef = useRef<number>(0);

    // Reset fired state whenever the profile id changes.
    const profileIdRef = useRef<string | undefined>(profile?.id);
    if (profile && profile.id !== profileIdRef.current) {
        profileIdRef.current = profile.id;
        firedRef.current = new Set();
        lastRecurringMinRef.current = 0;
    }

    // --- Accelerating tick sound ---
    // Tick tempo increases as time runs out: normal (1s) → faster beat in last
    // 60s, building excitement like seconds-mode rhythm.
    const { tickEnabled, voiceEnabled } = useSettingsStore();
    const tickTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        if (!isRunning || !tickEnabled) {
            if (tickTimerRef.current) {
                clearTimeout(tickTimerRef.current);
                tickTimerRef.current = null;
            }
            return;
        }

        const remainingSec = count;

        let intervalMs: number;
        if (remainingSec <= 3) {
            intervalMs = 80;
        } else if (remainingSec <= 10) {
            intervalMs = 150;
        } else if (remainingSec <= 30) {
            intervalMs = 250;
        } else if (remainingSec <= 60) {
            intervalMs = 400;
        } else {
            intervalMs = 1000;
        }

        playKidSound('tick');

        tickTimerRef.current = setTimeout(() => undefined, intervalMs);

        return () => {
            if (tickTimerRef.current) {
                clearTimeout(tickTimerRef.current);
                tickTimerRef.current = null;
            }
        };
    }, [count, isRunning, totalMinutes, tickEnabled]);

    // --- Sub-alarms + recurring warnings ---
    useEffect(() => {
        if (!profile || !isRunning) return;

        const countStart = totalMinutes * 60;
        const elapsedSec = countStart - count;
        const elapsedMin = elapsedSec / 60;

        // Fire sub-alarms at their thresholds
        for (const sub of profile.subAlarms) {
            if (sub.atElapsedMin >= totalMinutes) continue;
            if (firedRef.current.has(sub.id)) continue;
            if (elapsedMin >= sub.atElapsedMin) {
                firedRef.current.add(sub.id);
                fireSubAlarm(sub, audio, voiceEnabled);
            }
        }

        // Fire recurring warning every N minutes
        if (profile.recurringWarning && profile.recurringWarning.intervalMin > 0) {
            const interval = profile.recurringWarning.intervalMin;
            const currentBucket = Math.floor(elapsedMin / interval) * interval;
            if (currentBucket > 0 && currentBucket > lastRecurringMinRef.current) {
                lastRecurringMinRef.current = currentBucket;
                fireRecurringWarning(currentBucket, audio, profile.recurringWarning, voiceEnabled);
            }
        }
    }, [profile, totalMinutes, count, isRunning, audio, voiceEnabled]);

    // Reset fired set when the timer is reset to its initial value.
    useEffect(() => {
        if (!isRunning && count >= totalMinutes * 60) {
            firedRef.current = new Set();
            lastRecurringMinRef.current = 0;
        }
    }, [isRunning, count, totalMinutes]);

    if (!profile) return null;
    const countStart = totalMinutes * 60;
    const elapsedSec = countStart - count;
    const elapsedMin = elapsedSec / 60;
    const passed = profile.subAlarms
        .filter((s) => s.atElapsedMin <= elapsedMin)
        .sort((a, b) => a.atElapsedMin - b.atElapsedMin);
    return passed.length > 0 ? passed[passed.length - 1] : null;
};
